/**
 * Content Dialogs for GameDetail
 * - Cover Art Dialog (IGDB + SteamGridDB search)
 * - Section/Organizer Dialog (create/edit organizers)
 * - Challenge Dialog (create/edit custom challenges)
 * - Bulk Move Dialog (move achievements between sections/DLCs)
 */
import { useState } from "react";
import {
  Loader2,
  Check,
  Search,
  Image,
  Layers,
  Target,
  Move,
  Gamepad2,
  Package,
  Timer,
  Calendar,
  Trophy,
  Square,
  CheckSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarWithYear } from "@/components/ui/calendar-with-year";
import { format } from "date-fns";

/**
 * Cover Art Dialog - Search IGDB and SteamGridDB for cover art
 */
export function CoverArtDialog({
  open,
  onOpenChange,
  game,
  searchQuery,
  onSearchQueryChange,
  onSearch,
  loading,
  results,
  sourceFilter,
  onSourceFilterChange,
  getFilteredResults,
  selectedCover,
  onSelectCover,
  onApply,
  uploading,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Image className="w-5 h-5 text-orange-400" />
            Get Cover Art
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Search IGDB and SteamGridDB for high-quality cover art
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          {/* Search */}
          <div className="flex gap-2">
            <Input
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onSearch()}
              placeholder="Search game name..."
              className="bg-black/50 border-white/10 flex-1"
              data-testid="cover-search-input"
            />
            <Button 
              onClick={onSearch} 
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-500"
              data-testid="cover-search-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          
          {/* Source Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/50">Source:</span>
            <div className="flex gap-1">
              {["all", "igdb", "steamgriddb"].map((source) => (
                <Button
                  key={source}
                  size="sm"
                  variant={sourceFilter === source ? "default" : "ghost"}
                  onClick={() => onSourceFilterChange(source)}
                  className={`h-7 px-3 text-xs ${
                    sourceFilter === source 
                      ? "bg-orange-600 hover:bg-orange-500" 
                      : "text-white/60 hover:text-white"
                  }`}
                  data-testid={`cover-filter-${source}`}
                >
                  {source === "all" ? "All Sources" : source === "igdb" ? "IGDB" : "SteamGridDB"}
                </Button>
              ))}
            </div>
            {results.combined?.length > 0 && (
              <span className="text-xs text-white/40 ml-auto">
                {results.igdb?.length || 0} IGDB • {results.steamgriddb?.length || 0} SteamGridDB
              </span>
            )}
          </div>
          
          {/* Current cover */}
          {game?.boxArtUrl && (
            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
              <img 
                src={game.boxArtUrl} 
                alt="Current" 
                className="w-16 h-24 object-contain rounded bg-black/50"
              />
              <div>
                <p className="text-xs text-white/50">Current Box Art</p>
                <p className="text-sm text-white">{game.name}</p>
              </div>
            </div>
          )}
          
          {/* Results */}
          {getFilteredResults().length > 0 && (
            <div className="space-y-4">
              {getFilteredResults().map((gameResult) => (
                <div key={gameResult.source_id} className="border border-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="font-medium text-white">{gameResult.name}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase ${
                      gameResult.source === "igdb" 
                        ? "bg-purple-500/20 text-purple-300" 
                        : "bg-blue-500/20 text-blue-300"
                    }`}>
                      {gameResult.source}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {gameResult.covers.map((cover, idx) => (
                      <div 
                        key={idx}
                        onClick={() => onSelectCover(cover)}
                        className={`cursor-pointer rounded-lg p-2 transition-all ${
                          selectedCover?.url === cover.url 
                            ? "bg-orange-500/20 border-2 border-orange-500" 
                            : "bg-white/5 border border-white/10 hover:bg-white/10"
                        }`}
                        data-testid={`cover-option-${gameResult.source_id}-${idx}`}
                      >
                        <div className="aspect-[2/3] bg-black/50 rounded overflow-hidden mb-2">
                          <img 
                            src={cover.preview_url} 
                            alt={`${cover.type} ${cover.size}`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-white/50 uppercase">{cover.type}</p>
                          <p className="text-xs text-white">{cover.dimensions}</p>
                          {cover.author && (
                            <p className="text-[9px] text-white/30 truncate">by {cover.author}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Selected preview */}
          {selectedCover && (
            <div className="border border-orange-500/30 bg-orange-500/10 rounded-lg p-4">
              <p className="text-sm text-orange-300 mb-3">Selected Cover Preview</p>
              <div className="flex items-start gap-4">
                <div className="w-32 aspect-[2/3] bg-black/50 rounded overflow-hidden">
                  <img 
                    src={selectedCover.preview_url} 
                    alt="Selected"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-white/50">Source: <span className={`${
                    selectedCover.source === "igdb" ? "text-purple-300" : "text-blue-300"
                  }`}>{selectedCover.source?.toUpperCase()}</span></p>
                  <p className="text-xs text-white/50">Type: <span className="text-white">{selectedCover.type}</span></p>
                  <p className="text-xs text-white/50">Size: <span className="text-white">{selectedCover.dimensions}</span></p>
                  {selectedCover.author && (
                    <p className="text-xs text-white/50">Author: <span className="text-white">{selectedCover.author}</span></p>
                  )}
                  <p className="text-[10px] text-white/30 mt-2 break-all">{selectedCover.url}</p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-white/20"
            data-testid="cover-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onApply}
            disabled={!selectedCover || uploading}
            className="bg-orange-600 hover:bg-orange-500"
            data-testid="cover-dialog-apply"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Apply Cover
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Section/Organizer Dialog - Create or edit custom organizers
 */
export function SectionDialog({
  open,
  onOpenChange,
  isEditing,
  sectionName,
  onSectionNameChange,
  sectionDescription,
  onSectionDescriptionChange,
  onSave,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-cyan-400" />
            {isEditing ? 'Edit Organizer' : 'Create New Organizer'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Organizer Name</Label>
            <Input
              placeholder="e.g., Story Missions, Collectibles, Multiplayer..."
              value={sectionName}
              onChange={(e) => onSectionNameChange(e.target.value)}
              className="bg-black/50 border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label>Description <span className="text-white/40 text-xs font-normal">(optional)</span></Label>
            <Textarea
              placeholder="Add notes about how this organizer should be used..."
              value={sectionDescription || ""}
              onChange={(e) => onSectionDescriptionChange(e.target.value)}
              className="bg-black/50 border-white/10 min-h-[80px] resize-none"
              data-testid="section-description-input"
            />
          </div>
          <p className="text-xs text-white/50">
            Organizers help organize base game achievements into custom groups. 
            Use the bulk move feature to assign achievements to organizers.
          </p>
          <Button
            onClick={onSave}
            className="w-full bg-cyan-600 hover:bg-cyan-700"
          >
            {isEditing ? 'Update Section' : 'Create Section'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Challenge Dialog - Create or edit custom challenges
 */
export function ChallengeDialog({
  open,
  onOpenChange,
  challenge,
  onChallengeChange,
  isEditing,
  achievements,
  onSave,
}) {
  // Helper to format timer display
  const getTimerHours = () => Math.floor((challenge?.timerSeconds || 0) / 3600) || "";
  const getTimerMinutes = () => Math.floor(((challenge?.timerSeconds || 0) % 3600) / 60) || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-orange-400" />
            {isEditing ? 'Edit Challenge' : 'Create Custom Challenge'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4 max-h-[60vh] overflow-y-auto">
          <div className="space-y-2">
            <Label>Challenge Name *</Label>
            <Input
              placeholder="e.g., Complete game without dying..."
              value={challenge?.name || ""}
              onChange={(e) => onChallengeChange({ ...challenge, name: e.target.value })}
              className="bg-black/50 border-white/10"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Describe the challenge..."
              value={challenge?.description || ""}
              onChange={(e) => onChallengeChange({ ...challenge, description: e.target.value })}
              className="bg-black/50 border-white/10 min-h-[80px]"
            />
          </div>

          <div className="space-y-2">
            <Label>Image URL (optional)</Label>
            <Input
              placeholder="https://example.com/challenge-image.jpg"
              value={challenge?.imageUrl || ""}
              onChange={(e) => onChallengeChange({ ...challenge, imageUrl: e.target.value })}
              className="bg-black/50 border-white/10"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Timer className="w-4 h-4 text-blue-400" />
                Timer (optional)
              </Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder="Hours"
                  min="0"
                  value={getTimerHours()}
                  onChange={(e) => {
                    const hours = parseInt(e.target.value) || 0;
                    const currentMinutes = getTimerMinutes() || 0;
                    const newSeconds = (hours * 3600) + (currentMinutes * 60);
                    onChallengeChange({ ...challenge, timerSeconds: newSeconds || null });
                  }}
                  className="bg-black/50 border-white/10"
                />
                <Input
                  type="number"
                  placeholder="Min"
                  min="0"
                  max="59"
                  value={getTimerMinutes()}
                  onChange={(e) => {
                    const minutes = parseInt(e.target.value) || 0;
                    const currentHours = getTimerHours() || 0;
                    const newSeconds = (currentHours * 3600) + (minutes * 60);
                    onChallengeChange({ ...challenge, timerSeconds: newSeconds || null });
                  }}
                  className="bg-black/50 border-white/10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-400" />
                Due Date (optional)
              </Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={challenge?.dueDate ? format(new Date(challenge.dueDate), 'MM/dd/yyyy') : ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                      const [month, day, year] = val.split('/');
                      const parsed = new Date(`${year}-${month}-${day}T12:00:00`);
                      if (!isNaN(parsed.getTime())) {
                        onChallengeChange({ ...challenge, dueDate: parsed.toISOString() });
                      }
                    } else if (val === "") {
                      onChallengeChange({ ...challenge, dueDate: null });
                    }
                  }}
                  className="bg-black/50 border-white/10 flex-1"
                  placeholder="MM/DD/YYYY"
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="icon" className="bg-black/50 border-white/10 shrink-0">
                      <Calendar className="w-4 h-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="bg-[#0A0A0A] border-white/10 p-0">
                    <CalendarWithYear
                      mode="single"
                      selected={challenge?.dueDate ? new Date(challenge.dueDate) : undefined}
                      onSelect={(date) => onChallengeChange({ ...challenge, dueDate: date?.toISOString() || null })}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-yellow-400" />
              Prerequisite Achievements (optional)
            </Label>
            <p className="text-xs text-white/50">Challenge can only be marked complete after these achievements are unlocked</p>
            
            {/* Search filter for achievements */}
            <Input
              placeholder="Search achievements..."
              className="bg-black/50 border-white/10 text-sm"
              onChange={(e) => {
                const list = e.target.closest('.space-y-2').querySelector('.achievement-list');
                if (list) {
                  list.querySelectorAll('[data-achievement]').forEach(item => {
                    const name = item.dataset.achievement.toLowerCase();
                    const desc = item.dataset.description?.toLowerCase() || '';
                    const searchTerm = e.target.value.toLowerCase();
                    item.style.display = (name.includes(searchTerm) || desc.includes(searchTerm)) ? '' : 'none';
                  });
                }
              }}
            />
            
            <div className="achievement-list max-h-48 overflow-y-auto border border-white/10 rounded-lg p-2 space-y-1">
              {!achievements || achievements.length === 0 ? (
                <p className="text-xs text-white/30 text-center py-2">No achievements available</p>
              ) : (
                [...(achievements || [])]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((ach) => {
                    const prereqs = challenge?.prerequisiteAchievementIds || [];
                    const isSelected = prereqs.includes(ach.id);
                    return (
                      <button
                        key={ach.id}
                        data-achievement={ach.name}
                        data-description={ach.description || ''}
                        onClick={() => {
                          const newPrereqs = isSelected
                            ? prereqs.filter(id => id !== ach.id)
                            : [...prereqs, ach.id];
                          onChallengeChange({ ...challenge, prerequisiteAchievementIds: newPrereqs });
                        }}
                        className={`w-full text-left px-2 py-2 rounded text-xs transition-colors ${
                          isSelected
                            ? 'bg-yellow-500/20 border border-yellow-500/30'
                            : 'hover:bg-white/5 border border-transparent'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {isSelected 
                            ? <CheckSquare className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" /> 
                            : <Square className="w-4 h-4 text-white/40 flex-shrink-0 mt-0.5" />}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={isSelected ? 'text-yellow-400' : 'text-white/80'}>{ach.name}</span>
                              {ach.isUnlocked && (
                                <span className="text-[10px] bg-green-500/20 text-green-400 px-1 rounded">Unlocked</span>
                              )}
                              {ach.score > 0 && (
                                <span className="text-[10px] text-white/40">{ach.score}G</span>
                              )}
                            </div>
                            {ach.description && (
                              <p className="text-white/40 text-[11px] mt-0.5 line-clamp-2">{ach.description}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })
              )}
            </div>
            
            {/* Show selected count */}
            {(challenge?.prerequisiteAchievementIds || []).length > 0 && (
              <p className="text-xs text-yellow-400">
                {(challenge?.prerequisiteAchievementIds || []).length} achievement(s) selected
              </p>
            )}
          </div>

          <Button
            onClick={onSave}
            className="w-full bg-orange-600 hover:bg-orange-700"
          >
            {isEditing ? 'Update Challenge' : 'Create Challenge'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Bulk Move Dialog - Move achievements between sections/DLCs
 */
export function BulkMoveDialog({
  open,
  onOpenChange,
  selectedCount,
  sections,
  dlcs,
  onMove,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl w-[95vw] max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="bulk-move-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Move className="w-5 h-5 text-blue-400" />
            Move {selectedCount} Achievement(s)
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4 overflow-y-auto flex-1 pr-1">
          <p className="text-sm text-white/50">Select a destination for the selected achievements:</p>
          
          {/* Move to Base Game (unsectioned) */}
          <Button
            variant="outline"
            onClick={() => onMove("base", null)}
            className="w-full justify-start border-white/10 hover:bg-white/5"
          >
            <Gamepad2 className="w-4 h-4 mr-2 text-blue-400" />
            Base Game (unsectioned)
          </Button>

          {/* Move to Organizers - sorted alphabetically. Each organizer with
              subgroups exposes those as indented sub-buttons so achievements
              can be dropped straight into a specific subgroup. */}
          {sections.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-white/50">Organizers</Label>
              {[...sections].sort((a, b) => a.name.localeCompare(b.name)).map((section) => (
                <div key={section.id} className="space-y-1">
                  <Button
                    variant="outline"
                    onClick={() => onMove("section", section.id)}
                    className="w-full justify-start border-cyan-500/30 hover:bg-cyan-500/10 h-auto py-2 whitespace-normal text-left"
                    data-testid={`bulk-move-section-${section.id}`}
                  >
                    <Layers className="w-4 h-4 mr-2 text-cyan-400 shrink-0" />
                    <span className="flex-1 break-words">{section.name}</span>
                    <span className="ml-auto text-xs text-white/40 shrink-0">Ungrouped</span>
                  </Button>
                  {(section.subsections || []).map((sub) => (
                    <Button
                      key={sub.id}
                      variant="outline"
                      onClick={() => onMove("subsection", sub.id, section.id)}
                      className="w-full justify-start border-white/10 hover:bg-cyan-500/5 pl-8 h-auto py-1.5 min-h-[2rem] whitespace-normal text-left"
                      data-testid={`bulk-move-subsection-${sub.id}`}
                    >
                      <span
                        className="w-2 h-2 rounded-full mr-2 shrink-0"
                        style={{ backgroundColor: sub.color || "#06B6D4" }}
                      />
                      <span className="text-sm break-words flex-1">{sub.name}</span>
                    </Button>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Move to DLCs */}
          {dlcs.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-white/50">DLCs</Label>
              {dlcs.map((dlc) => (
                <Button
                  key={dlc.id}
                  variant="outline"
                  onClick={() => onMove("dlc", dlc.id)}
                  className="w-full justify-start border-purple-500/30 hover:bg-purple-500/10"
                >
                  <Package className="w-4 h-4 mr-2 text-purple-400" />
                  {dlc.name}
                </Button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
